import { NavLink, Outlet, useNavigate } from "react-router-dom";
import {
  LayoutGrid,
  FileText,
  Users,
  Receipt,
  Sparkles,
  Settings as Cog,
  LogOut,
  Plus,
  Building2,
  FileImage,
  Package,
  Repeat,
  Scale,
  UserCheck,
  ShieldCheck,
  Code2,
  Wallet,
  BookOpen,
  FileClock,
  Store,
  Radar,
  BadgeCheck,
  Droplets,
  LineChart as LineChartIcon,
  Bell,
  Rocket,
  BarChart3,
  KeyRound,
  Search as SearchIcon,
  Upload,
  ExternalLink,
} from "lucide-react";
import Logo from "@/components/Logo";
import { useAuth } from "@/lib/auth";
import { Button } from "@/components/ui/button";
import CommandPalette from "@/components/CommandPalette";
import OnboardingWizard from "@/components/OnboardingWizard";
import { useEffect, useRef, useState } from "react";
import { api } from "@/lib/api";
import { toast } from "sonner";

// Navigation is grouped by the KB "understanding → decisions → actions → records"
// hierarchy. Insight-first surfaces sit at the top; records are lower.
const navGroups = [
  {
    label: "Decide",
    items: [
      { to: "/app/dashboard", label: "Dashboard", icon: LayoutGrid, tid: "nav-dashboard" },
      { to: "/app/cfo", label: "AI CFO", icon: Sparkles, tid: "nav-cfo" },
      { to: "/app/signals", label: "Signals", icon: Bell, tid: "nav-signals" },
      { to: "/app/performance", label: "Performance", icon: BarChart3, tid: "nav-performance" },
    ],
  },
  {
    label: "Plan",
    items: [
      { to: "/app/liquidity", label: "Cash & Liquidity", icon: Droplets, tid: "nav-liquidity" },
      { to: "/app/forecast", label: "Forecast", icon: LineChartIcon, tid: "nav-forecast" },
      { to: "/app/scenarios", label: "Scenario Lab", icon: LineChartIcon, tid: "nav-scenarios" },
      { to: "/app/fundraising", label: "Fundraising", icon: Rocket, tid: "nav-fundraising" },
      { to: "/app/saas", label: "SaaS Spend", icon: Package, tid: "nav-saas" },
      { to: "/app/licenses", label: "Licenses", icon: KeyRound, tid: "nav-licenses" },
    ],
  },
  {
    label: "Money in",
    items: [
      { to: "/app/invoices", label: "Invoices", icon: FileText, tid: "nav-invoices" },
      { to: "/app/clients", label: "Clients", icon: Users, tid: "nav-clients" },
      { to: "/app/products", label: "Products", icon: Package, tid: "nav-products" },
      { to: "/app/recurring", label: "Recurring", icon: Repeat, tid: "nav-recurring" },
    ],
  },
  {
    label: "Money out",
    items: [
      { to: "/app/expenses", label: "Expenses", icon: Receipt, tid: "nav-expenses" },
      { to: "/app/bills", label: "Bills", icon: FileClock, tid: "nav-bills" },
      { to: "/app/vendors", label: "Vendors", icon: Store, tid: "nav-vendors" },
      { to: "/app/receipts", label: "Receipts", icon: FileImage, tid: "nav-receipts" },
      { to: "/app/banks", label: "Banks", icon: Building2, tid: "nav-banks" },
    ],
  },
  {
    label: "Books",
    items: [
      { to: "/app/accounting", label: "Accounting", icon: Scale, tid: "nav-accounting" },
      { to: "/app/budgets", label: "Budgets", icon: Wallet, tid: "nav-budgets" },
      { to: "/app/approvals", label: "Approvals", icon: UserCheck, tid: "nav-approvals" },
    ],
  },
  {
    label: "Grow",
    items: [
      { to: "/app/memberships", label: "Memberships", icon: BadgeCheck, tid: "nav-memberships" },
    ],
  },
  {
    label: "Learn",
    items: [
      { to: "/app/learn", label: "Learn", icon: BookOpen, tid: "nav-learn" },
      { to: "/app/docs", label: "Docs AI", icon: BookOpen, tid: "nav-docs" },
    ],
  },
  {
    label: "Admin",
    items: [
      { to: "/app/team", label: "Team", icon: ShieldCheck, tid: "nav-team" },
      { to: "/app/developer", label: "Developer", icon: Code2, tid: "nav-developer" },
      { to: "/app/migrate", label: "Import data", icon: Upload, tid: "nav-migrate" },
      { to: "/app/settings", label: "Settings", icon: Cog, tid: "nav-settings" },
    ],
  },
];

export default function AppShell() {
  const { user, organization, logout } = useAuth();
  const navigate = useNavigate();
  const [approvalsPending, setApprovalsPending] = useState(0);
  const approvalsToastShownRef = useRef(false);

  // Poll pending approvals every 60s so the sidebar badge stays fresh.
  useEffect(() => {
    let cancelled = false;
    const load = async () => {
      try {
        const r = await api.get("/approvals/pending-count");
        if (cancelled) return;
        const pending = r.data?.pending || 0;
        setApprovalsPending(pending);
        // One-time login toast — per user per day.
        if (pending > 0 && !approvalsToastShownRef.current && user?.id) {
          const key = `lemonade_approvals_toast_${user.id}_${new Date().toISOString().slice(0, 10)}`;
          if (!localStorage.getItem(key)) {
            approvalsToastShownRef.current = true;
            localStorage.setItem(key, "1");
            const suffix = pending === 1 ? "" : "s";
            toast(`${pending} approval${suffix} waiting`, {
              description: "Signals ready for your sign-off.",
              action: {
                label: "Review",
                onClick: () => navigate("/app/approvals"),
              },
              duration: 8000,
            });
          } else {
            // Session-scope guard so we don't toast on every 60s poll if
            // someone opens the app twice in the same day.
            approvalsToastShownRef.current = true;
          }
        }
      } catch {
        // Silent — badge just doesn't update.
      }
    };
    load();
    const id = setInterval(load, 60_000);
    return () => {
      cancelled = true;
      clearInterval(id);
    };
  }, [user?.id, navigate]);

  return (
    <div className="flex min-h-screen bg-[hsl(0_0%_98.5%)]">
      <CommandPalette />
      <OnboardingWizard />
      {/* Sidebar */}
      <aside className="hidden max-h-screen w-64 shrink-0 flex-col overflow-y-auto border-r border-border bg-white px-5 py-8 md:sticky md:top-0 md:flex">
        <div className="px-2">
          <Logo size={30} />
        </div>

        <Button
          onClick={() => navigate("/app/invoices/new")}
          data-testid="sidebar-new-invoice"
          className="mt-10 w-full justify-center rounded-full bg-primary py-5 text-sm font-medium text-primary-foreground hover:bg-primary/90"
        >
          <Plus className="mr-2 h-4 w-4" /> New invoice
        </Button>

        <button
          onClick={() => {
            const evt = new KeyboardEvent("keydown", { key: "k", ctrlKey: true, bubbles: true });
            window.dispatchEvent(evt);
          }}
          data-testid="sidebar-search"
          className="mt-3 flex w-full items-center justify-between gap-2 rounded-full border border-border bg-white px-4 py-2.5 text-xs text-muted-foreground hover:bg-accent"
        >
          <span className="flex items-center gap-2">
            <SearchIcon className="h-3.5 w-3.5" /> Search
          </span>
          <kbd className="rounded bg-muted px-1.5 py-0.5 text-[10px]">⌘K</kbd>
        </button>

        <nav className="mt-8 flex flex-col gap-1 overflow-y-auto pb-6">
          {navGroups.map((group, idx) => (
            <div key={group.label} className={idx === 0 ? "" : "mt-4"}>
              <div className="mb-2 px-3 text-[10px] font-medium uppercase tracking-[0.22em] text-muted-foreground">
                {group.label}
              </div>
              {group.items.map((item) => (
                <NavLink
                  key={item.to}
                  to={item.to}
                  data-testid={item.tid}
                  className={({ isActive }) =>
                    `group flex items-center gap-3 rounded-2xl px-3 py-2 text-sm transition-all ${
                      isActive
                        ? "bg-primary/15 text-foreground font-medium"
                        : "text-muted-foreground hover:bg-accent hover:text-foreground"
                    }`
                  }
                >
                  <item.icon className="h-4 w-4 stroke-[1.6]" />
                  <span className="flex-1">{item.label}</span>
                  {item.tid === "nav-approvals" && approvalsPending > 0 && (
                    <span
                      data-testid="approvals-pending-badge"
                      className="ml-auto flex h-5 min-w-[1.25rem] items-center justify-center rounded-full bg-rose-500 px-1.5 text-[10px] font-semibold text-white"
                    >
                      {approvalsPending > 99 ? "99+" : approvalsPending}
                    </span>
                  )}
                </NavLink>
              ))}
            </div>
          ))}
          {organization?.work_domain && (
            <a
              href={organization.work_api_base_url || `https://${organization.work_domain}`}
              target="_blank"
              rel="noopener noreferrer"
              data-testid="nav-work-sister-app"
              className="group mt-6 flex items-center gap-3 rounded-2xl border border-dashed border-border px-3 py-2.5 text-sm text-muted-foreground transition-all hover:border-foreground/40 hover:text-foreground"
              title={`Open ${organization.work_domain}`}
            >
              <ExternalLink className="h-4 w-4 stroke-[1.6]" />
              <span className="flex-1 truncate">{organization.name} Work</span>
              <span className="text-[9px] uppercase tracking-wider">sister app</span>
            </a>
          )}
          {user?.is_platform_admin && (
            <NavLink
              to="/app/platform"
              data-testid="nav-platform"
              className={({ isActive }) =>
                `group mt-6 flex items-center gap-3 rounded-2xl px-3 py-2.5 text-sm transition-all ${
                  isActive
                    ? "bg-foreground text-background font-medium"
                    : "text-muted-foreground hover:bg-accent hover:text-foreground"
                }`
              }
            >
              <Radar className="h-4 w-4 stroke-[1.6]" />
              Platform
              <span className="ml-auto rounded-full bg-primary/70 px-2 py-0.5 text-[9px] font-medium uppercase tracking-wider text-foreground">Admin</span>
            </NavLink>
          )}
        </nav>

        <div className="mt-auto rounded-3xl border border-border p-4">
          <div className="truncate text-sm font-medium">{user?.display_name}</div>
          <div className="mt-0.5 truncate text-xs text-muted-foreground">
            {organization?.name}
          </div>
          <button
            onClick={logout}
            data-testid="logout-button"
            className="mt-3 inline-flex items-center gap-2 text-xs text-muted-foreground hover:text-foreground"
          >
            <LogOut className="h-3.5 w-3.5" /> Sign out
          </button>
        </div>
      </aside>

      {/* Main */}
      <main className="flex min-h-screen flex-1 flex-col" data-testid="app-main">
        <Outlet />
      </main>
    </div>
  );
}
